/**
 * The icon set.
 *
 * ## Why hand-drawn paths and not an icon package
 *
 * The app needs about two dozen icons. A package brings a dependency, a tree-shaking
 * question and a licence for that, and none of the popular ones would have solved the
 * actual problem — which was not that the icons were ugly but that they were **text**.
 *
 * Every icon in this app was an emoji or a Unicode glyph: `⌂ ⌕ ▤ ▦ ◉ ✚ ⚙ › ♥ 🎤 ⏪ ⏸ ▶`.
 * That has four failures, and the app had all four:
 *
 *   - **They render differently on every platform.** An emoji is a font, and the Tizen
 *     panel, Windows and iOS each ship their own. `🛰️` on the pairing screen is a
 *     photographic colour glyph on one and a flat outline on another.
 *   - **They cannot be coloured.** A colour emoji ignores `color`, so an icon could not
 *     participate in a focus state or a disabled state.
 *   - **They cannot be sized reliably.** Glyph metrics differ per font, so `⌂` and `⌕` sat
 *     at different optical sizes in the same nav bar at the same `font-size`.
 *   - **Some are not available at all.** A missing glyph is a tofu box, at three metres,
 *     with no fallback.
 *
 * ## The drawing rules
 *
 * A 24×24 box, `currentColor`, 1.75 stroke, round caps and joins. Stroked rather than
 * filled because a filled icon at TV distance reads as a solid blob; the two exceptions
 * are `play`/`pause`, which are filled because they are the one place a solid shape is the
 * conventional and clearer reading.
 *
 * `focusable="false"` and `aria-hidden` by default: an icon beside a label is decoration
 * and a screen reader announcing "home icon, Home" is noise. Pass a `title` for the case
 * where the icon *is* the only label, and it becomes an `img` with an accessible name
 * instead.
 */

const STROKE = {
  home: 'M3 11.2 12 4l9 7.2V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4.1-4.1',
  /*
   * A film strip with sprocket holes, not the bordered grid this was first drawn as.
   *
   * `movies` and `series` sit next to each other in the navigation, and the first attempt
   * gave one a 3×2 grid and the other a 2×2 grid — at nav size they read as the same icon
   * twice. Rendered side by side that was obvious immediately and invisible in the source.
   */
  movies: 'M4 4.5h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1zM8 4.5v15M16 4.5v15M3 9.5h5M3 14.5h5M16 9.5h5M16 14.5h5',
  series: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  live: 'M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 8.2a5.4 5.4 0 0 1 0 7.6M5.4 5.4a9.3 9.3 0 0 0 0 13.2M18.6 5.4a9.3 9.3 0 0 1 0 13.2',
  plus: 'M12 5v14M5 12h14',
  settings: 'M4 7h16M4 17h16M9.5 4.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4zM14.5 14.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z',
  chevronRight: 'M9.5 5.5 16 12l-6.5 6.5',
  chevronDown: 'M5.5 9.5 12 16l6.5-6.5',
  back: 'M19 12H5M11.5 5.5 5 12l6.5 6.5',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M4.5 12.5 9.5 17.5 19.5 6.5',
  profiles: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M16.5 4.6a3.5 3.5 0 0 1 0 6.8M18 13.4a6.5 6.5 0 0 1 3.5 5.8',
  phone: 'M7.5 2.5h9A1.5 1.5 0 0 1 18 4v16a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V4a1.5 1.5 0 0 1 1.5-1.5zM10.5 18.5h3',
  tv: 'M3.5 6.5h17A1.5 1.5 0 0 1 22 8v9a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 17V8a1.5 1.5 0 0 1 1.5-1.5zM8 21.5h8M9 6.5 12 3l3 3.5',
  /* The screen, open at the corner the signal arrives from — the conventional cast
   * reading. The first attempt crossed broadcast arcs with an arrow and resolved as
   * neither. */
  pair: 'M15.5 19.5h4A1.5 1.5 0 0 0 21 18v-12A1.5 1.5 0 0 0 19.5 4.5h-15A1.5 1.5 0 0 0 3 6v2.5M3 12.5a7 7 0 0 1 7 7M3 16.5a3 3 0 0 1 3 3M3.2 19.7v.1',
  heart: 'M12 20.2s-7.4-4.6-7.4-9.6a4.2 4.2 0 0 1 7.4-2.7 4.2 4.2 0 0 1 7.4 2.7c0 5-7.4 9.6-7.4 9.6z',
  mic: 'M12 3a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21',
  rewind: 'M11 6.5 4.5 12l6.5 5.5zM20 6.5 13.5 12l6.5 5.5z',
  forward: 'M4 6.5 10.5 12 4 17.5zM13 6.5 19.5 12 13 17.5z',
  lock: 'M5.5 11h13a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1zM8 11V7a4 4 0 0 1 8 0v4',
  volume: 'M4 9.5h3L11.5 5v14L7 14.5H4a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5zM15.5 9.5a3.5 3.5 0 0 1 0 5M18.5 6.5a7.5 7.5 0 0 1 0 11',
  mute: 'M4 9.5h3L11.5 5v14L7 14.5H4a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5zM16 10l5 4M21 10l-5 4',
  subtitles: 'M3.5 5.5h17A1.5 1.5 0 0 1 22 7v10a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 17V7a1.5 1.5 0 0 1 1.5-1.5zM6 14h5M14 14h4',
  audio: 'M9 18V6l10-2v12M9 18a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zM19 16a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z',
  fullscreen: 'M4 9V4.5h5M20 9V4.5h-5M4 15v4.5h5M20 15v4.5h-5',
  /* The same four corners with the brackets turned inward. Drawn as its own icon rather
     than reusing `fullscreen` with a changed label: a button whose glyph is identical in
     both states tells the viewer nothing about which one they are in, and the label is
     read by roughly nobody. */
  fullscreenExit: 'M9 4.5V9H4.5M15 4.5V9h4.5M9 19.5V15H4.5M15 19.5V15h4.5',
  info: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 11v5.5M12 7.6v.1',
} as const;

/** Filled, because a solid transport control is the conventional and clearer reading. */
const FILLED = {
  play: 'M7.5 4.8v14.4a.7.7 0 0 0 1.07.6l11.2-7.2a.7.7 0 0 0 0-1.2L8.57 4.2a.7.7 0 0 0-1.07.6z',
  pause: 'M7 4.5h3.2a.6.6 0 0 1 .6.6v13.8a.6.6 0 0 1-.6.6H7a.6.6 0 0 1-.6-.6V5.1a.6.6 0 0 1 .6-.6zM13.8 4.5H17a.6.6 0 0 1 .6.6v13.8a.6.6 0 0 1-.6.6h-3.2a.6.6 0 0 1-.6-.6V5.1a.6.6 0 0 1 .6-.6z',
  heartFilled: 'M12 20.4s-7.6-4.7-7.6-9.8a4.3 4.3 0 0 1 7.6-2.8 4.3 4.3 0 0 1 7.6 2.8c0 5.1-7.6 9.8-7.6 9.8z',
  record: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z',
} as const;

export type IconName = keyof typeof STROKE | keyof typeof FILLED;

export interface IconProps {
  name: IconName;
  /**
   * Edge length in `em`, so an icon scales with the text it sits beside rather than
   * needing a per-context pixel size. The television gets larger icons for free through
   * its larger root font size — the same mechanism the type scale uses.
   */
  size?: number;
  /** Supply only when the icon carries meaning no adjacent text already carries. */
  title?: string;
  className?: string;
}

export function Icon({ name, size = 1.25, title, className }: IconProps) {
  const filled = name in FILLED;
  const d = filled ? FILLED[name as keyof typeof FILLED] : STROKE[name as keyof typeof STROKE];

  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      viewBox="0 0 24 24"
      width={`${size}em`}
      height={`${size}em`}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? undefined : 1.75}
      strokeLinecap={filled ? undefined : 'round'}
      strokeLinejoin={filled ? undefined : 'round'}
      // `focusable` is an IE/Edge-legacy attribute that Tizen's Chromium still honours in
      // some builds; without it an inline SVG can land in the tab order, which on a remote
      // means a focus stop that looks like nothing at all.
      focusable="false"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      <path d={d} />
    </svg>
  );
}
